import { FieldShell } from '@today/ui-components'
import { Button, Dialog, Input } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Copy of {@link ApiKeyRenameDialogView}; each omitted key falls back to English. */
export interface ApiKeyRenameDialogLabels {
	title: string
	name: string
	submit: string
	cancel: string
	close: string
}

const DEFAULT_LABELS: ApiKeyRenameDialogLabels = {
	title: 'Rename API key',
	name: 'Name',
	submit: 'Save',
	cancel: 'Cancel',
	close: 'Close',
}

/**
 * Props for {@link ApiKeyRenameDialogView} — controlled: the app owns the draft and its validation.
 *
 * @property error - A server refusal shown inside the still-open dialog.
 */
export interface ApiKeyRenameDialogViewProps {
	isOpen: boolean
	name: string
	nameError?: string | null
	error?: string | null
	isBusy?: boolean
	onNameChange: (name: string) => void
	onSubmit: () => void
	onCancel: () => void
	labels?: Partial<ApiKeyRenameDialogLabels>
}

/** Edits an existing key's name (organism, TO-516). */
export function ApiKeyRenameDialogView(props: ApiKeyRenameDialogViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={text.title}
			labels={{ close: text.close }}
		>
			<form
				noValidate
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault()
					props.onSubmit()
				}}
			>
				{props.error ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{props.error}
					</p>
				) : null}
				<FieldShell label={text.name} error={props.nameError ?? undefined}>
					<Input value={props.name} onChange={(e) => props.onNameChange(e.target.value)} />
				</FieldShell>
				<div className="mt-1 flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{text.submit}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{text.cancel}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}
